import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import CtaLink from '@/components/CtaLink';
import BusinessPricingColumn from '@/components/billing/business/BusinessPricingColumn';
import { CheckIcon } from '@/components/billing/business/parts';
import { useLanguage } from '@/components/LanguageContext';
import { getActivePricing } from '@/api/billingService';
import { contactSales } from '@/lib/salesContact';
import { withCta } from '@/lib/ctaTracking';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * PricingTableV3 — las tres columnas de precios de la home del rediseño.
 *
 * DOS DECISIONES QUE NO SIGUEN AL DISEÑO, y las dos a conciencia:
 *
 * 1. **Los importes no están escritos aquí.** El diseño hornea «29,90 € / mes» y
 *    «240 € / año»; esos números salen de `getActivePricing()`
 *    (`/billing/active-pricing`), como en todas las demás superficies. La regla
 *    está escrita en `BusinessPlanCard.jsx` y en la landing V2, y existe porque
 *    la tarifa ha cambiado cuatro veces (launch → v2 → v3 → v4): un importe en
 *    el código se queda viejo en silencio y nadie lo ve hasta que un cliente
 *    paga otra cosa.
 *
 * 2. **La columna Business es `<BusinessPricingColumn>`**, la versión web de la
 *    card de suscripción (pantalla «Web · página de precios» de
 *    `temp_docs/CardBusiness.html`). Comparte piezas y cálculo
 *    (`cartBreakdown`) con la card de la app, así que no es otra copia del
 *    mismo cálculo. El toggle Mensual/Anual vive DENTRO de la columna.
 *
 * Las columnas Gratis y Enterprise siguen la misma pantalla del diseño: ahí no
 * hay cálculo que compartir, solo una lista y un botón.
 *
 * La columna Gratis dice lo que da conectar la ficha de Google, SIN
 * dispositivos: todavía no está decidido cómo entran los dispositivos en la
 * landing, así que aquí no se mencionan. Por eso su botón va a registro y no a
 * la tienda.
 */

/**
 * @param {object}  props
 * @param {boolean} [props.conEncabezado]  el `<h2>` + subtítulo de la sección.
 *   `true` en la home, donde la tabla es una sección entre otras; `false` en la
 *   página de precios, donde ese mismo texto ya es el `<h1>`.
 */
export default function PricingTableV3({ conEncabezado = true }) {
  const { t, language } = useLanguage();
  const c = pickLandingV3Content(language).precios;

  // Mismo par de estados que la landing V2: el número de locales y el periodo
  // los posee el contenedor, porque `BusinessPlanCard` es un componente puro.
  const [locales, setLocales] = useState(1);
  const [billing, setBilling] = useState('monthly');

  const { data: pricing } = useQuery({
    queryKey: ['billing', 'active-pricing'],
    queryFn: getActivePricing,
  });

  const trialDays = pricing?.trialDays ?? 0;

  return (
    <section
      id="precios"
      className="bg-v3-surface border-y border-v3-line px-6 py-16 md:py-[72px]"
    >
      <div className="max-w-[1180px] mx-auto">
        {/* En la página de precios el mismo texto ya es el `<h1>` de la página,
            así que aquí se calla: repetirlo dejaría el titular dos veces
            seguidos y un `<h2>` que duplica al `<h1>`. En la home, donde la
            tabla es una sección más entre otras, sí lo necesita. */}
        {conEncabezado ? (
          <>
            <h2 className="text-[28px] md:text-[34px] leading-[1.15] tracking-[-.025em] font-bold text-center text-v3-ink">
              {c.titulo}
            </h2>
            <p className="text-[16.5px] leading-[1.6] text-v3-body text-center mt-3 max-w-[56ch] mx-auto">
              {c.subtitulo}
            </p>
          </>
        ) : null}

        {/* `items-start`: las tres columnas tienen alturas muy distintas (la de
            Business trae el selector, el desglose y la lista larga) y estirarlas
            dejaría metros de blanco bajo las otras dos. Proporciones 1 · 1,35 · 1
            como el diseño; por debajo de ~920 px se apilan solas. */}
        <div className="mx-auto mt-9 flex max-w-[1120px] flex-wrap items-start gap-5">
          <PlanSimple
            plan={c.gratis}
            className="flex-[1_1_280px]"
            insigniaClase="bg-green-100 text-green-800"
            checkStroke="#15803d"
            boton={
              <CtaLink
                as={Link}
                cta="landing.v3.planes.gratis"
                to={withCta('/register', 'landing.v3.planes.gratis')}
                className="flex min-h-[48px] items-center justify-center rounded-[14px] bg-stone-100 text-[15px] font-semibold text-stone-900 no-underline transition-colors hover:bg-stone-200"
              >
                {c.gratis.cta}
              </CtaLink>
            }
          />

          <BusinessPricingColumn
            className="flex-[1.35_1_360px]"
            pricing={pricing}
            seats={locales}
            billing={billing}
            onSeatsChange={setLocales}
            onBillingChange={setBilling}
            onCheckout={() => {
              window.location.href = withCta('/register', 'landing.v3.planes.trial');
            }}
            ctaId="landing.v3.planes.trial"
            trialDays={trialDays}
            trialAvailable={trialDays > 0}
            enterpriseLink={(
              <p className="m-0 text-[13px] text-stone-600">
                {t('billing.sub.enterpriseMore', { max: pricing?.maxSeats || 30 })}{' '}
                <CtaLink
                  as="button"
                  type="button"
                  cta="landing.v3.planes.enterprise-mas-30"
                  onClick={contactSales}
                  className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-amber-800 underline hover:text-amber-900"
                >
                  {t('billing.sub.enterpriseLink')}
                </CtaLink>
              </p>
            )}
          />

          <PlanSimple
            plan={c.enterprise}
            className="flex-[1_1_280px]"
            insigniaClase="bg-stone-100 text-stone-700"
            checkStroke="#57534e"
            boton={
              <CtaLink
                as="button"
                cta="landing.v3.planes.ventas"
                onClick={contactSales}
                className="flex min-h-[48px] w-full cursor-pointer items-center justify-center rounded-[14px] border-0 bg-stone-900 font-[inherit] text-[15px] font-semibold text-white transition-colors hover:bg-black"
              >
                {c.enterprise.cta}
              </CtaLink>
            }
          />
        </div>
      </div>
    </section>
  );
}

/**
 * Las columnas que no calculan nada: un precio literal, una lista y un botón.
 * Sirve para Gratis y Enterprise, que en el diseño son la misma tarjeta
 * con distinto color de acento.
 */
function PlanSimple({ plan, className = '', insigniaClase, checkStroke, boton }) {
  return (
    <section className={`flex min-w-0 flex-col gap-5 rounded-[20px] border border-stone-200 bg-white p-7 text-stone-900 ${className}`}>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="m-0 text-lg font-bold">{plan.nombre}</h2>
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${insigniaClase}`}>{plan.insignia}</span>
        </div>
        <p className="m-0 text-sm text-stone-600">{plan.tagline}</p>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[40px] font-extrabold leading-none tracking-[-0.03em]">{plan.precio}</span>
        <span className="text-sm text-stone-600">{plan.nota}</span>
      </div>

      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm leading-[1.45] text-stone-700">
            <CheckIcon size={16} strokeWidth={2.6} stroke={checkStroke} className="mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {boton}
    </section>
  );
}
